import React, { useEffect, useState } from 'react';
import { Alert, Button, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import ResponsiveDialog from '../common/ResponsiveDialog';
import e2iApi from '../../services/e2iApi';
import type { E2IConfig, EnquiryWorkspace } from '../../types/e2i';
import { errorText } from '../../utils/e2i';
import EnquiryForm, { EnquiryFormState, emptyEnquiryForm, enquiryProblems, inputFromForm } from './EnquiryForm';

interface Props {
  open: boolean;
  companyId: number;
  config: E2IConfig | null;
  onClose: () => void;
  onCreated: (ws: EnquiryWorkspace) => void;
}

/** A new enquiry (saved as a draft; it gets its number then). */
const NewEnquiryDialog: React.FC<Props> = ({ open, companyId, config, onClose, onCreated }) => {
  const [form, setForm] = useState<EnquiryFormState>(emptyEnquiryForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(emptyEnquiryForm());
    setBusy(false);
    setError(null);
    setTried(false);
  }, [open]);

  const problems = enquiryProblems(form, false);

  const create = async () => {
    setTried(true);
    if (problems.length) return;
    setBusy(true);
    setError(null);
    try {
      const ws = await e2iApi.createEnquiry(companyId, inputFromForm(form));
      onCreated(ws);
    } catch (e: any) {
      setError(errorText(e, 'The enquiry could not be created'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth aria-labelledby="e2i-new-enquiry-title">
      <DialogTitle id="e2i-new-enquiry-title">New enquiry</DialogTitle>
      <DialogContent dividers>
        <EnquiryForm idPrefix="e2i-new" companyId={companyId} config={config} value={form} onChange={setForm} />
        {tried && problems.length ? <Alert severity="warning" sx={{ mt: 2 }}>{problems.join('; ')}.</Alert> : null}
        {error ? <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>{error}</Alert> : null}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={() => { void create(); }} disabled={busy}>{busy ? 'Creating…' : 'Create enquiry'}</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default NewEnquiryDialog;
